import Dexie, { Table } from 'dexie';
import logger from '../../logger';
import {
  isBrowser,
  setLastSelectedItemIDInCollection,
} from '../../utils/helpers.ts';
import { LOCAL_DATABASE_KEYS, STORAGE_KEYS } from '../../constants';

const { DRYCHROMECLASS_V2, DRYCHROMECLASSSMALL_V2 } = STORAGE_KEYS;

// Setup new database
let notesDB = null;

export const setupNotesDB = () => {
  if (typeof window === 'undefined') return;

  notesDB = new Dexie('drycloudNotes');
  // Must be >= the highest version ever written for this DB in any deployment.
  // IndexedDB throws VersionError if the requested version is lower than the on-disk version.

  // Version 21: base schema (must remain defined for Dexie upgrade path)
  notesDB.version(21).stores({
    [LOCAL_DATABASE_KEYS.NOTES]: `note_id,owner_id,notebook_id,date_created,date_modified,date_opened,app_created_with,trash,version`,
    [LOCAL_DATABASE_KEYS.USER_SETTINGS]: `id`,
    [LOCAL_DATABASE_KEYS.AREAS_OF_FOCUS]: `area_of_focus_collection_id,owner_id,title,selected_note,default_area_of_focus_collection,date_created,date_modified,app_created_with,version`,
    [LOCAL_DATABASE_KEYS.NOTEBOOKS]: `notebook_id,notebook_name,owner_id,date_created,date_modified,app_created_with,default_notebook`,
    [LOCAL_DATABASE_KEYS.USER_PANEL_CONFIGS]: `id`,
    [LOCAL_DATABASE_KEYS.DRY_CHROME]: `modelId,model,utcDateTime`,
    [LOCAL_DATABASE_KEYS.SYSTEM]: `name`,
  });

  // Version 22: add pendingWrites queue for offline note saves
  notesDB.version(22).stores({
    [LOCAL_DATABASE_KEYS.NOTES]: `note_id,owner_id,notebook_id,date_created,date_modified,date_opened,app_created_with,trash,version`,
    [LOCAL_DATABASE_KEYS.USER_SETTINGS]: `id`,
    [LOCAL_DATABASE_KEYS.AREAS_OF_FOCUS]: `area_of_focus_collection_id,owner_id,title,selected_note,default_area_of_focus_collection,date_created,date_modified,app_created_with,version`,
    [LOCAL_DATABASE_KEYS.NOTEBOOKS]: `notebook_id,notebook_name,owner_id,date_created,date_modified,app_created_with,default_notebook`,
    [LOCAL_DATABASE_KEYS.USER_PANEL_CONFIGS]: `id`,
    [LOCAL_DATABASE_KEYS.DRY_CHROME]: `modelId,model,utcDateTime`,
    [LOCAL_DATABASE_KEYS.SYSTEM]: `name`,
    [LOCAL_DATABASE_KEYS.PENDING_WRITES]: `id,type,createdAt`,
  });

  // Version 23: add note_history table for offline diff preview caching
  notesDB.version(23).stores({
    [LOCAL_DATABASE_KEYS.NOTES]: `note_id,owner_id,notebook_id,date_created,date_modified,date_opened,app_created_with,trash,version`,
    [LOCAL_DATABASE_KEYS.USER_SETTINGS]: `id`,
    [LOCAL_DATABASE_KEYS.AREAS_OF_FOCUS]: `area_of_focus_collection_id,owner_id,title,selected_note,default_area_of_focus_collection,date_created,date_modified,app_created_with,version`,
    [LOCAL_DATABASE_KEYS.NOTEBOOKS]: `notebook_id,notebook_name,owner_id,date_created,date_modified,app_created_with,default_notebook`,
    [LOCAL_DATABASE_KEYS.USER_PANEL_CONFIGS]: `id`,
    [LOCAL_DATABASE_KEYS.DRY_CHROME]: `modelId,model,utcDateTime`,
    [LOCAL_DATABASE_KEYS.SYSTEM]: `name`,
    [LOCAL_DATABASE_KEYS.PENDING_WRITES]: `id,type,createdAt`,
    [LOCAL_DATABASE_KEYS.NOTE_HISTORY]: `history_id,note_id,doc_version,date_created`,
  });

  notesDB.open().catch(function (err) {
    console.error('Failed to open db: ' + (err.stack || err));
  });
};

// Delete the database and all other local browser storage.
export const deleteDatabases = async () => {
  // 1. Synchronous storage — clear immediately before any async work.
  try { localStorage.clear(); } catch (_) {}
  try { sessionStorage.clear(); } catch (_) {}

  // 2. Cookies — expire every cookie visible on the current path/domain.
  try {
    document.cookie.split(';').forEach((cookie) => {
      const name = cookie.split('=')[0].trim();
      document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;
    });
  } catch (_) {}

  // 3. Cache API (service-worker caches).
  try {
    if (typeof caches !== 'undefined') {
      const keys = await caches.keys();
      await Promise.all(keys.map((key) => caches.delete(key)));
    }
  } catch (_) {}

  // 4. IndexedDB — close the active Dexie connection first.
  //    indexedDB.deleteDatabase() blocks until all open connections close;
  //    calling notesDB.close() prevents the indefinite hang.
  try {
    if (notesDB && notesDB.isOpen()) {
      notesDB.close();
    }
    // Race the delete against a 3 s timeout so the caller is never blocked.
    await Promise.race([
      Dexie.delete('drycloudNotes'),
      new Promise((resolve) => setTimeout(resolve, 3000)),
    ]);
    logger.info('Database successfully deleted');
  } catch (error) {
    logger.error(error, 'Could not delete database');
  }
};

// Delete only the IndexedDB database, leaving cookies and localStorage intact.
export const deleteIndexedDBOnly = async () => {
  try {
    if (notesDB && notesDB.isOpen()) {
      notesDB.close();
    }
    await Promise.race([
      Dexie.delete('drycloudNotes'),
      new Promise((resolve) => setTimeout(resolve, 3000)),
    ]);
    logger.info('IndexedDB successfully deleted');
  } catch (error) {
    logger.error(error, 'Could not delete IndexedDB');
    throw error;
  }
};

export const writeNoteToClientDatabase = async ({ noteObject }) => {
  logger.info(
    {
      noteObjectData: Object.keys(noteObject),
    },
    'noteDB: writeNote - properites'
  );
  const { note_id } = noteObject;

  try {
    // Use put() for upsert semantics — update() is a no-op if the record doesn't exist
    const id = await notesDB.notes.put(noteObject);
    logger.info(id, 'Wrote note to IDB');

    // Get the noteRecord
    const note = await notesDB.notes.where('note_id').equals(note_id).first();
    return note;
  } catch (error) {
    logger.error(error, 'writeNoteToClientDatabase Error');
    return error;
  }
};

export const deleteNoteFromClientDatabase = async ({ noteId }) => {
  logger.info({ noteId }, 'noteDB: deleteNote - noteId');

  // Delete a note
  const id = await notesDB.notes.delete(noteId);
  console.log(id, 'Removed note ID');
};

export const writeUserSettings = async ({ userSettings }) => {
  logger.info(
    {
      userSettingsObjectData: Object.keys(userSettings),
    },
    'noteDB: writeUserSettings - properites'
  );

  // Give userSettings an id
  userSettings.id = 1;

  // Write a userSettingsRecord
  const userSettingsId = await notesDB.userSettings.put(userSettings);
  console.log(userSettingsId, 'Added user settings');

  // Get the userSettingsRecord
  const userSettingsResults = await notesDB.userSettings
    .where('id')
    .equals(userSettingsId);
  return userSettingsResults;
};

export const hydrateNotesClientDatabase = async ({ notes }) => {
  try {
    await notesDB.notes.bulkPut(notes);
    return notes;
  } catch (error) {
    logger.error(error, `hydrateNotesClientDatabase bulkPut Error`);
    throw error;
  }
};

export const readNotesTable = async ({ ownerId }) => {
  try {
    const notes = await notesDB.notes
      .where('owner_id')
      .equals(ownerId)
      .toArray();
    return notes;
  } catch (error) {
    logger.error(error, `readNotesTable Error`);
    return error;
  }
};

export const readUserSettingsTable = async () => {
  try {
    const userSetting = await notesDB.userSettings
      .where('id')
      .equals(1)
      .first((response) => (response ? response : {}));
    return userSetting;
  } catch (error) {
    logger.error(error, `readUserSettingsTable Error`);
    return error;
  }
};

export const hydrateAreasOfFocusClientDatabase = async ({ areasOfFocus }) => {
  try {
    await notesDB.areasOfFocus.bulkPut(areasOfFocus);
    return areasOfFocus;
  } catch (error) {
    logger.error(error, `hydrateAreasOfFocusClientDatabase bulkPut Error`);
    return error;
  }
};

/**
 *
 * @param { ownerId } param0
 * @returns returns an array of area of focus collection objects
 */
export const readAreasOfFocusCollectionsTable = async ({ ownerId }) => {
  try {
    const areasOfFocus = await notesDB.areasOfFocus
      .where('owner_id')
      .equals(ownerId)
      .toArray();
    return areasOfFocus;
  } catch (error) {
    logger.error(error, `readAreasOfFocusTable Error`);
    return error;
  }
};

/**
 *
 * @param { ownerId } param0
 * @returns returns a single of area of focus collection object
 */
export const fetchAreaOfFocusCollection = async ({
  areaOfFocusCollectionId,
}) => {
  logger.info(areaOfFocusCollectionId, 'Fetch area of focus Id');

  // Get the areaOfFocus Record
  const areaOfFocusData = await notesDB.areasOfFocus
    .where('area_of_focus_collection_id')
    .equals(areaOfFocusCollectionId)
    .first((response) => (response ? response : {}));
  return areaOfFocusData;
};

/**
 *
 * @param { ownerId } param0
 * @returns Creates and returns an area of focus object
 */
export const writeAreaOfFocus = async ({ areaOfFocus }) => {
  logger.info(
    {
      areaOfFocusObjectData: Object.keys(areaOfFocus),
    },
    'noteDB: writeAreaOfFocus - properites'
  );
  const { area_of_focus_collection_id } = areaOfFocus;

  // Write an Area Of Focus record
  try {
    const id = await notesDB.areasOfFocus.put(areaOfFocus);
    logger.info(id, 'Added area of focus');

    // Get the areaOfFocus Record
    const areaOfFocusData = await fetchAreaOfFocusCollection({
      areaOfFocusCollectionId: area_of_focus_collection_id,
    });
    return areaOfFocusData;
  } catch (error) {
    logger.error(error, `writeAreaOfFocus Error`);
    return error;
  }
};

/**
 * Remove a note from an area of focus collection
 * @param {*} param0
 */
export const deleteAreaOfFocusFromLocalDB = async ({ collectionId }) => {
  logger.info({ collectionId }, 'noteDB: deleteAreaOfFocusFromLocalDB');
  try {
    await notesDB.areasOfFocus.delete(collectionId);
    logger.info({ collectionId }, 'Deleted area of focus from local DB');
  } catch (error) {
    logger.error(error, 'deleteAreaOfFocusFromLocalDB Error');
  }
};

export const removeNoteItemFromAreaOfFocusCollectionFromLocalDB = async ({
  areaOfFocusCollectionId,
  noteId,
}) => {
  logger.info(
    { areaOfFocusCollectionId, noteId },
    'noteDB: removeNoteItemFromAreaOfFocusCollectionFromLocalDB'
  );

  // Read the areaOfFocus
  const areaOfFocusCollection = await fetchAreaOfFocusCollection({
    areaOfFocusCollectionId,
  });
  const { AOFCollection } = setLastSelectedItemIdInCollection(
    areaOfFocusCollection,
    noteId
  );
  // Delete a note from order_of_items and selected_stack
  const id = await writeAreaOfFocus({ areaOfFocus: AOFCollection });
  logger.info(noteId, 'Removed noteId');
};

export const hydrateNotebooksClientDatabase = async ({ notebooks }) => {
  try {
    await notesDB.notebooks.bulkPut(notebooks);
    return notebooks;
  } catch (error) {
    logger.error(error, `hydrateNotebooksClientDatabase bulkPut Error`);
    return error;
  }
};

export const readNotebooksTable = async ({ ownerId }) => {
  try {
    const notebooks = await notesDB.notebooks
      .where('owner_id')
      .equals(ownerId)
      .toArray();
    return notebooks;
  } catch (error) {
    logger.error(error, `readNotebooksTable Error`);
    return error;
  }
};

export const writeNotebook = async ({ notebook }) => {
  logger.info(
    {
      notebookObjectData: Object.keys(notebook),
    },
    'noteDB: writeNotebook - properites'
  );
  const { notebook_id } = notebook;

  // Write a notebook
  try {
    const id = await notesDB.notebooks.put(notebook);
    console.log(id, 'Added notebook');

    // Get the notebook Record
    const notebookData = await notesDB.notebooks
      .where('notebook_id')
      .equals(notebook_id);
    return notebookData;
  } catch (error) {
    logger.error(error, `writeNotebook Error`);
    return error;
  }
};

export const hydrateUserPanelConfigsClientDatabase = async ({
  userPanelConfigs,
}) => {
  // Give userPanelConfigs an id
  userPanelConfigs.id = 1;

  try {
    await notesDB.userPanelConfigs.put(userPanelConfigs);
    return userPanelConfigs;
  } catch (error) {
    logger.error(error, `hydrateUserPanelConfigsClientDatabase put Error`);
    return error;
  }
};

// Write Single Panel Config
export const writeUserPanelConfigsClientDatabase =
  hydrateUserPanelConfigsClientDatabase;

export const readUserPanelConfigsTable = async () => {
  try {
    const userPanelConfigs = await notesDB.userPanelConfigs
      .where('id')
      .equals(1)
      .first((response) => (response ? response : {}));
    return userPanelConfigs;
  } catch (error) {
    logger.error(error, `readUserPanelConfigsTable Error`);
    return error;
  }
};

export const readChromeModelClientDatabase = async () => {
  try {
    // Will return an array of models, if there is no data, then it will return an index with an undefined value
    let dryChromeModels = await notesDB.dryChrome.bulkGet([
      DRYCHROMECLASS_V2,
      DRYCHROMECLASSSMALL_V2,
    ]);

    // check each item in the array, if the value is undefined, remove it from the array
    const [model, modelSmall] = dryChromeModels;

    // Reset dryChromeModels to an empty array
    if (model === undefined && modelSmall === undefined) {
      dryChromeModels = [];
    }

    logger.debug(dryChromeModels, `readChromeModelClientDatabase`);

    return dryChromeModels;
  } catch (error) {
    logger.error(error, `readChromeModelClientDatabase Error`);
    return [];
    // return error;
  }
};

// Write Single Chrome Model
export const writeChromeModelClientDatabase = async ({ modelId, model }) => {
  const storedModel = {
    modelId,
    model,
  };

  try {
    await notesDB.dryChrome.put(storedModel);
    return model;
  } catch (error) {
    logger.error(error, `writeChromeModelClientDatabase put Error`);
    return error;
  }
};

// Write BULK Chrome Models
export const hydrateChromeModelsClientDatabase = async ({
  userChromeModels,
}) => {
  try {
    await notesDB.dryChrome.bulkPut(userChromeModels);
    return userChromeModels;
  } catch (error) {
    logger.error(error, `hydrateChromeModelsClientDatabase bulkPut Error`);
    return error;
  }
};

export const writeSystemValue = async ({
  name,
  utcDateTime = '',
  data = {},
}) => {
  logger.info('writeSystemValue', {
    name,
    utcDateTime,
    data,
  });

  try {
    await notesDB.system.put({
      name,
      utcDateTime,
      data,
    });

    const systemValue = await notesDB.system
      .where('name')
      .equals(name)
      .first((response) => (response ? response : {}));
    return systemValue;
  } catch (error) {
    logger.error(error, 'writeSystemValue Error');
    return error;
  }
};

export const readSystemValue = async ({ name }) => {
  logger.info('readSystemValue', {
    name,
  });

  const systemValue = await notesDB.system
    .where('name')
    .equals(name)
    .first((response) => (response ? response : {}));
  return systemValue;
};

// ─── Offline write queue ──────────────────────────────────────────────────────

export const writePendingWrite = async ({ id, type, payload }) => {
  try {
    await notesDB.pendingWrites.put({ id, type, payload, createdAt: new Date().toISOString() });
    logger.info({ id, type }, 'writePendingWrite: queued offline write');
  } catch (error) {
    logger.error(error, 'writePendingWrite Error');
  }
};

export const readPendingWrites = async () => {
  try {
    return await notesDB.pendingWrites.toArray();
  } catch (error) {
    logger.error(error, 'readPendingWrites Error');
    return [];
  }
};

export const deletePendingWrite = async ({ id }) => {
  try {
    await notesDB.pendingWrites.delete(id);
    logger.info({ id }, 'deletePendingWrite: removed from queue');
  } catch (error) {
    logger.error(error, 'deletePendingWrite Error');
  }
};

// Initialize the offline database
setupNotesDB();
